/**
 * Typed API error + a single response guard, so retry/permission decisions key on the
 * HTTP status instead of substring-matching hand-written error strings (audit finding 180).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail?: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Throw a typed {@link ApiError} when a fetch Response is not ok.
 *
 * Reads the body once, preferring a JSON `detail` field (FastAPI's error shape) for the
 * message and falling back to `fallbackMessage` or the status text. No-op on ok responses,
 * so call sites can do `await assertOk(response); return response.json();`.
 */
export async function assertOk(response: Response, fallbackMessage?: string): Promise<void> {
  if (response.ok) return;

  let detail: unknown;
  let message = fallbackMessage ?? `Request failed: ${response.status} ${response.statusText}`;

  try {
    const body = await response.clone().json();
    detail = body?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      message = detail;
    } else if (detail != null) {
      message = JSON.stringify(detail);
    }
  } catch {
    // Non-JSON body — keep the fallback/status message.
  }

  throw new ApiError(response.status, message, detail);
}
